import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a stored date for display.
 *
 * `incorporation_date` and `afr_due_date` are date-only strings — "1988-08-31",
 * no time and no zone — and `new Date` reads those as UTC midnight. Rendered in
 * a zone behind UTC that is the previous evening, so a company incorporated on
 * the 31st shows as the 30th. The parts are split out and handed to a local
 * `Date` instead, so the date displayed is the date stored.
 *
 * A value carrying a time (`created_at`) is an instant rather than a date, and
 * is left to `Date` to place in the viewer's zone.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);

  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

/**
 * A file size a person can read at a glance.
 *
 * Rounded hard on purpose: the question a list of attachments answers is "is
 * that the two-page scan or the forty-page one", and three significant figures
 * of kilobytes answers it no better while being harder to skim.
 */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
